import styled from 'styled-components';
import isEqual from 'lodash/isEqual';
import { Link } from 'react-router-dom';
import Utils from '../../../shared/utils';
import ProductSeries from '../../../shared/product/ProductSeries';
import ProductAuthors from '../../../shared/product/ProductAuthors';
import ProductGenres from '../../../shared/product/ProductGenres';
import ProductAge from '../../../shared/product/ProductAge';
import ProductPublisher from '../../../shared/product/ProductPublisher';
import { ProductReleaseDate } from '../../../shared/product/ProductReleaseDate';
import ProductLanguage from '../../../shared/product/ProductLanguage';
import { PreviewButton } from '../../../shared/preview/PreviewButton';
import { MATERIAL_ICON_SETTINGS } from '../../../shared/common/Button/Icon';
import { LibxStatisticsWithStockDetails } from '../../libx/shared/LibxStatisticsWithStockDetails';
import LibxAddToCollectionButton from '../../libx/shared/LibxAddToCollectionButton';
import LibxTitleInfoLine from '../shared/LibxTitleInfoLine';
import { LOANFORMAT_EBOOK_OBJECT, LOANFORMAT_EBOOKS } from '../../../store/loanFormat/actions';

import ProductStatusLine from './ProductStatusLine';
import { ProductCover } from './ProductCover';
import { Button } from '../../../shared/common/Button';
import { useTranslation } from 'react-i18next';

const StPackshotWrapper = styled.div`
    width: 180px;
    height: 150px;
    display: table-cell;
    vertical-align: top;
    border-bottom: 1px solid #e6e6e6;
    padding: 15px 0;
    position: relative;

    :hover {
        .icn-preview i {
            opacity: 1;
        }
    }
`;

const StLocalProductCover = styled(ProductCover)`
    width: 80%;
    height: auto !important;
`;

const StProductListBottom = styled.div`
    position: absolute;
    bottom: 20px;
    height: 20px;
    width: 100%;
`;

const StPreviewButtonWrapper = styled.div`
    .icn-preview {
        left: calc(50% - 33px);
        top: 22px;
        height: 30px;
        position: absolute;
        line-height: 20px;
        text-align: center;
        width: 30px;
        cursor: pointer;
    }

    .icn-preview i {
        color: #7a7a7a;
        display: inline-block;
        float: left;
        font-size: 16px;
        height: 30px;
        line-height: 30px;
        opacity: 0;
        width: 30px;

        :hover {
            .ebooks {
                background: url('../../../../img/icons/icn-ebook-preview-hover.svg') no-repeat 0 0 / auto 30px;
            }
        }
    }

    .ebooks {
        background: url('../../../../img/icons/icn-ebook-preview.svg') no-repeat 0 0 / auto 30px;
    }
`;

interface Props {
    id?: string;
    product: any;
    publisher?: string;
    isYourTitle: boolean;
    isLibxchange: boolean;
    isInCollection: boolean;
    productLinkUri: string;
    updateProduct?: () => void;
    onAddToCollection?: () => void;
    onManageButtonClick?: (product: any) => void;
    loanFormat?: typeof LOANFORMAT_EBOOKS;
    shopLanguage: string | undefined;
}

const ListItem = (props: Props) => {
    const { t } = useTranslation();
    if (!props.product) return null;
    const productLinkUri = props.productLinkUri || '';
    const loanFormatKey = props.loanFormat || LOANFORMAT_EBOOKS;
    const product = {
        ...props.product,
        loanFormat: LOANFORMAT_EBOOK_OBJECT.name,
        publisher: props.publisher || props.product.publisher
    };
    if (isEqual(product.ageGroup, 'All')) {
        delete product.ageGroup;
    }

    const combinedTitle = `${product.title} ${product.subTitle ? product.subTitle : ''}`;

    //TODO: Remove feature toggle and add the Product Language Component for real

    return (
        <div className="product-list-row">
            <StPackshotWrapper>
                <Link to={productLinkUri}>
                    <StLocalProductCover title={product.title} coverUrl={product.coverUrl} minSizeInPx={288} />
                </Link>
                <StPreviewButtonWrapper>
                    {props.isYourTitle ? null : <PreviewButton product={product} />}
                </StPreviewButtonWrapper>
            </StPackshotWrapper>
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        <div className="tc tcw-25">
                            <ProductSeries
                                product={product}
                                linked={false}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={props.shopLanguage}
                            />
                            <p title={combinedTitle} className="label">
                                <Link to={productLinkUri}>
                                    <strong>{Utils.truncate(combinedTitle)}</strong>
                                </Link>
                            </p>
                            <ProductAuthors
                                product={product}
                                linked={false}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={props.shopLanguage}
                            />
                        </div>
                        <div className="tc tcw-20">
                            <p>
                                {t('components.productFormat.eBook.none', {
                                    pages: product.pages
                                })}
                            </p>
                            <ProductGenres
                                product={product}
                                linked={false}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={props.shopLanguage}
                            />
                            <ProductAge
                                product={product}
                                linked={false}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={props.shopLanguage}
                            />

                            <ProductLanguage
                                product={product}
                                loanFormatKey={loanFormatKey}
                                withoutLink={true}
                                label={product.language}
                                shopLanguage={props.shopLanguage}
                            />
                        </div>
                        <div className="tc tcw-25">
                            <p>{!props.isLibxchange ? product.id : product.isbn13}</p>
                            <ProductReleaseDate product={product} />
                            <ProductPublisher
                                product={product}
                                linked={props.isLibxchange}
                                // isLibx={props.isLibxchange}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={props.shopLanguage}
                            />
                            <p>{t('components.productUsageConditions.simultaneousAccess')}</p>
                        </div>
                        <div className="tc tcw-20" style={{ width: '155px', float: 'right' }}>
                            {!props.isInCollection ? null : (
                                <Button
                                    icon={MATERIAL_ICON_SETTINGS}
                                    text={t('manage')}
                                    onClick={() =>
                                        props.onManageButtonClick ? props.onManageButtonClick(props.product) : null
                                    }
                                    className="manage-button"
                                />
                            )}
                            {!props.isLibxchange ? null : (
                                <LibxAddToCollectionButton
                                    updateProduct={props.updateProduct}
                                    product={product}
                                    onAddToCollection={props.onAddToCollection}
                                    productGroupId={props.id}
                                />
                            )}
                        </div>
                    </div>
                    <StProductListBottom>
                        {props.isLibxchange ? (
                            <LibxStatisticsWithStockDetails
                                activeLibraries={product.activeLibraries}
                                copyAmount={product.copyAmount}
                                stockDetails={product.stockDetails}
                            />
                        ) : null}
                        {!props.isYourTitle ? null : <ProductStatusLine status={product.activeStatus || {}} />}
                        {!props.isInCollection ? null : (
                            <LibxTitleInfoLine isLibxTitle={product.fromLibXchange || false} />
                        )}
                    </StProductListBottom>
                </div>
            </div>
        </div>
    );
};

export default ListItem;
